import { action } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Group } from "@shared/models/group"
import type { User } from "@shared/models/user"
import { isApple } from "@shared/unified-tiptap/util/is-apple"
import groupUnlockCopyLinkAction from "cc-frontend/lib/actions/group/unlock/GROUP_UNLOCK_COPY_LINK"
import groupUnlockSubmitRequestAction from "cc-frontend/lib/actions/group/unlock/GROUP_UNLOCK_SUBMIT_REQUEST"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type NotifierService from "cc-frontend/services/notifier"
import type Rpc from "cc-frontend/services/rpc"
import { EMAIL_REGEX } from "cc-frontend/utils/email"
import { lockedTeacherCount } from "cc-frontend/utils/group"
import styles from "./teacher.module.css"

export interface UnlockGroupFormTeacherSignature {
  Args: {
    closeModal: () => void
    model: {
      user: User
      group?: Group
    }
  }
}

export default class UnlockGroupFormTeacher extends Component<UnlockGroupFormTeacherSignature> {
  readonly styles = styles

  @service declare notifier: NotifierService
  @service declare rpc: Rpc
  @service declare dispatcher: Dispatcher

  @tracked
  adminEmail = ""
  @tracked
  schoolName = ""
  @tracked
  anythingElse = ""

  @tracked submitInProgress = false

  get canSubmit(): boolean {
    const isValidEmail = EMAIL_REGEX.test(this.adminEmail)
    const isValidSchoolName = this.args.model.group !== undefined || this.schoolName.trim() !== ""
    // Many requests we get are teachers entering their own email, which is not useful.
    const isOwnEmail =
      this.adminEmail.toLowerCase() === this.args.model.user.attributes.email.toLowerCase()
    return isValidEmail && isValidSchoolName && !isOwnEmail
  }

  @action
  setAdminEmail(adminEmail: string) {
    this.adminEmail = adminEmail
  }

  @action
  setSchoolName(schoolName: string) {
    this.schoolName = schoolName
  }

  @action
  setAnythingElse(anythingElse: string) {
    this.anythingElse = anythingElse
  }

  @action
  submit(event: MouseEvent) {
    event.preventDefault()
    if (!this.canSubmit) return

    const groupTitle = this.args.model.group?.attributes.title ?? this.schoolName
    const adminEmail = this.adminEmail

    this.submitInProgress = true
    this.rpc
      .group_request_unlock_teacher({
        adminEmail,
        groupTitle,
        anythingElse: this.anythingElse ?? "",
        ...(this.args.model.group
          ? {
              groupId: this.args.model.group.id,
              teacherCount: lockedTeacherCount(this.args.model.group),
            }
          : {}),
      })
      .then((resp) => {
        if (resp.responseOk) {
          this.notifier.success("", {
            title: "Thank you!",
            message: `We'll reach out to ${adminEmail} soon!`,
            duration: 3000,
          })
          this.args.closeModal()
        } else {
          this.notifier.success("", {
            title: `Something went wrong!`,
            message: "Please try again later.",
            duration: 10000,
            type: "is-error",
          })
        }
      })
      .catch((error) => {
        console.error(error)
        this.notifier.success("", {
          title: `Something went wrong!`,
          message: "Please try again later.",
          duration: 10000,
          type: "is-error",
        })
      })
      .finally(() => {
        later(() => {
          if (this.isDestroyed || this.isDestroying) return
          this.submitInProgress = false
        }, 250)
      })

    this.dispatcher.dispatchPreparedAction(
      groupUnlockSubmitRequestAction({
        form: "teacher",
        group: this.args.model.group,
        groupTitle,
        adminEmail,
      })
    )
  }

  @action
  showCopyToast() {
    this.notifier.success("", {
      title: "Scheduling link copied!",
      message: `Press ${isApple ? "Command(⌘) + v" : "Control + v"} to paste.`,
      duration: 6000,
    })

    this.dispatcher.dispatchPreparedAction(
      groupUnlockCopyLinkAction({
        form: "teacher",
        group: this.args.model.group,
      })
    )
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "User::UnlockGroupForm::Teacher": typeof UnlockGroupFormTeacher
  }
}
